import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiDollarSign, FiClock, FiAlertTriangle, FiCreditCard } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import { CONTRACTOR_AGING_DATA } from "./worksReportsData";

const SubcontractorPayablesAgingReport = () => {
  const gridRef = useRef(null);
  const [data] = useState(CONTRACTOR_AGING_DATA);
  const [searchTerm, setSearchTerm] = useState("");
  const [agingFilter, setAgingFilter] = useState("all");
  const [tradeFilter, setTradeFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalPayable = data.reduce((s, r) => s + (r.netPayable || 0), 0);
    const criticalOverdue = data
      .filter((r) => r.agingCategory === "Over 30 Days")
      .reduce((s, r) => s + (r.netPayable || 0), 0);
    const scheduledPayout = data
      .filter((r) => r.agingCategory === "8 – 15 Days")
      .reduce((s, r) => s + (r.netPayable || 0), 0);
    const totalAccounts = data.length;

    return {
      totalPayable,
      criticalOverdue,
      scheduledPayout,
      totalAccounts,
    };
  }, [data]);

  const uniqueTrades = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.leadTrade).filter(Boolean)));
  }, [data]);

  const getAgingBadge = (cat) => {
    if (cat === "Over 30 Days") {
      return { bg: "#fee2e2", color: "#b91c1c" };
    }
    if (cat === "16 – 30 Days") {
      return { bg: "#ffedd5", color: "#c2410c" };
    }
    if (cat === "8 – 15 Days") {
      return { bg: "#fef3c7", color: "#b45309" };
    }
    return { bg: "#f0fdf4", color: "#15803d" };
  };

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Contractor / Thekedar & Trade",
        field: "contractorName",
        colId: "contractorName",
        flex: 1.8,
        minWidth: 200,
        cellRenderer: (p) => (
          <div>
            <span style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</span>
            <div style={{ color: "#64748b", fontSize: "11px" }}>Trade: {p.data?.leadTrade}</div>
          </div>
        ),
      },
      {
        headerName: "Approved Bill No. & Certified Date",
        field: "approvedBillNo",
        colId: "approvedBillNo",
        flex: 1.5,
        minWidth: 170,
        cellRenderer: (p) => (
          <div>
            <span
              style={{
                fontWeight: 700,
                fontFamily: "monospace",
                color: "var(--color-primary, #0b6462)",
              }}
            >
              {p.value}
            </span>
            <div style={{ color: "#475569", fontSize: "11px" }}>
              Certified: {p.data?.certifiedDate}
            </div>
          </div>
        ),
      },
      {
        headerName: "Net Payable (PKR)",
        field: "netPayable",
        colId: "netPayable",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0f172a" }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Aging Category",
        field: "agingCategory",
        colId: "agingCategory",
        flex: 1.4,
        minWidth: 150,
        cellRenderer: (p) => {
          const badge = getAgingBadge(p.value);
          return (
            <span
              style={{
                background: badge.bg,
                color: badge.color,
                padding: "3px 10px",
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: "11px",
              }}
            >
              {p.value}
            </span>
          );
        },
      },
      {
        headerName: "Bank Account / Payment Mode",
        field: "paymentMode",
        colId: "paymentMode",
        flex: 1.8,
        minWidth: 200,
        cellRenderer: (p) => <span style={{ color: "#334155", fontWeight: 500 }}>{p.value}</span>,
      },
    ];
  }, []);

  // Filter logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matches =
          (row.contractorName && row.contractorName.toLowerCase().includes(q)) ||
          (row.leadTrade && row.leadTrade.toLowerCase().includes(q)) ||
          (row.approvedBillNo && row.approvedBillNo.toLowerCase().includes(q)) ||
          (row.paymentMode && row.paymentMode.toLowerCase().includes(q));
        if (!matches) {
          return false;
        }
      }

      if (agingFilter !== "all" && row.agingCategory !== agingFilter) {
        return false;
      }

      if (tradeFilter !== "all" && row.leadTrade !== tradeFilter) {
        return false;
      }

      if (dateRange.from && row.certifiedDate && row.certifiedDate < dateRange.from) {
        return false;
      }
      if (dateRange.to && row.certifiedDate && row.certifiedDate > dateRange.to) {
        return false;
      }

      return true;
    });
  }, [data, searchTerm, agingFilter, tradeFilter, dateRange]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
    }),
    [],
  );

  const toggleColumn = useCallback((colId) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      if (gridRef.current?.api) {
        gridRef.current.api.setColumnsVisible([colId], next[colId]);
      }
      return next;
    });
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Subcontractor_Payables_Aging_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("Subcontractor payables aging exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL APPROVED PAYABLES"
            value={`PKR ${(kpis.totalPayable / 1000000).toFixed(2)}M`}
            subtitle="Verified contractor obligations"
            icon={FiDollarSign}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="CRITICAL OVERDUE (>30 DAYS)"
            value={`PKR ${(kpis.criticalOverdue / 1000000).toFixed(2)}M`}
            subtitle="Immediate release priority"
            icon={FiAlertTriangle}
            color="#dc2626"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="SCHEDULED NEXT PAYOUT"
            value={`PKR ${(kpis.scheduledPayout / 1000000).toFixed(2)}M`}
            subtitle="8 – 15 days aging bucket"
            icon={FiClock}
            color="#0284c7"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="ACTIVE CONTRACTOR BILLS"
            value={`${kpis.totalAccounts} Bills`}
            subtitle="Accounts awaiting bank clearance"
            icon={FiCreditCard}
            color="#16a34a"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (Search First, then Date, then other filters) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Payables Aging Report"
            >
              Payables Aging Report
            </div>
            {/* Search Filter FIRST */}
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search contractor, bill, trade..."
            />

            {/* Date Filter SECOND */}
            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            {/* Aging Bucket Filter THIRD */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={agingFilter}
                  onChange={(e) => setAgingFilter(e.target.value)}
                >
                  <option value="all">All Aging Buckets</option>
                  <option value="0 – 7 Days">0 – 7 Days (In Process)</option>
                  <option value="8 – 15 Days">8 – 15 Days (Scheduled)</option>
                  <option value="16 – 30 Days">16 – 30 Days (Follow-up)</option>
                  <option value="Over 30 Days">Over 30 Days (Critical)</option>
                </select>
                <label>Aging Bucket</label>
              </div>
            </div>

            {/* Trade Filter */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={tradeFilter}
                  onChange={(e) => setTradeFilter(e.target.value)}
                >
                  <option value="all">All Trades</option>
                  {uniqueTrades.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <label>Trade</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              animateRows={true}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubcontractorPayablesAgingReport;
